import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { IconUser } from "@/assets/icons/geist/IconUser";
import { IconUserAdd } from "@/assets/icons/geist/IconUserAdd";
import { IconUsers } from "@/assets/icons/geist/IconUsers";
import { withGitpodSize } from "@/assets/icons/utils";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";
import { CreateGroupDialog } from "@/components/settings/organization/groups/CreateGroupDialog";
import { GroupsTab } from "@/components/settings/organization/groups/GroupsTab";
import { MembersTab } from "@/components/settings/organization/members/MembersTab";
import { CreateServiceAccountDialog } from "@/components/settings/organization/service-accounts/CreateServiceAccountDialog";
import { ServiceAccountsTab } from "@/components/settings/organization/service-accounts/ServiceAccountsTab";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMembership } from "@/hooks/use-membership";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useOrganization } from "@/queries/organization-queries";
import { CHANGE_PLAN_QUERY_KEY } from "@/routes/settings/organization/billing/Plan";
import { serviceAccountDetailsUrl } from "@/utils/service-accounts";
import IconCuteRobotBase from "central-icons/IconCuteRobot";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useState, type FC } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const IconCuteRobot = withGitpodSize(IconCuteRobotBase);

export const MembersPage: FC = () => {
    useDocumentTitle("Members");
    const { hasPermission, isOrgAdmin } = useResourcePermissions();
    const { membership } = useMembership();
    const { data: organization } = useOrganization();
    const location = useLocation();
    const [selectedTab, setSelectedTab] = useState<"members" | "groups" | "service-accounts">(
        location.hash === "#groups" ? "groups" : location.hash === "#service-accounts" ? "service-accounts" : "members",
    );
    const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
    const [showCreateServiceAccountDialog, setShowCreateServiceAccountDialog] = useState(false);
    const navigate = useNavigate();

    const isEnterpriseTier = organization?.tier === OrganizationTier.ENTERPRISE;
    const isFreePlan = organization?.tier === OrganizationTier.FREE_ONA || organization?.tier === OrganizationTier.FREE;
    const canCreateGroup = hasPermission(ResourceType.ORGANIZATION, membership?.organizationId || "", "group:create");
    const canCreateServiceAccount = hasPermission(
        ResourceType.ORGANIZATION,
        membership?.organizationId || "",
        "serviceaccount:create",
    );

    // Handle hash anchor navigation
    useEffect(() => {
        if (location.hash === "#groups") {
            setSelectedTab("groups");
        } else if (location.hash === "#service-accounts") {
            setSelectedTab("service-accounts");
        } else if (location.hash === "#people") {
            setSelectedTab("members");
        }
    }, [location.hash]);

    const onAddGroup = useCallback(() => {
        setShowCreateGroupDialog(true);
    }, []);

    const onAddServiceAccount = useCallback(() => {
        setShowCreateServiceAccountDialog(true);
    }, []);

    const handleTabChange = useCallback(
        (value: string) => {
            const newTab = value as "members" | "groups" | "service-accounts";
            setSelectedTab(newTab);
            // Update URL hash to reflect the selected tab
            const hash = newTab === "members" ? "#people" : newTab === "groups" ? "#groups" : "#service-accounts";
            navigate(`${location.pathname}${hash}`, { replace: true });
        },
        [navigate, location.pathname],
    );

    const showFreePlanBanner = isFreePlan && selectedTab === "members";
    const showEnterpriseBanner = !isEnterpriseTier && selectedTab === "groups";
    const showServiceAccountsBanner = isFreePlan && selectedTab === "service-accounts";

    return (
        <div className="mt-4 flex flex-col gap-4">
            {showFreePlanBanner && (
                <Banner
                    variant="info"
                    text="Upgrade to Core for up to 100 team members, unlimited parallel environments, and more."
                    action={{
                        text: "Upgrade",
                        onClick: () => navigate(`/settings/billing?${CHANGE_PLAN_QUERY_KEY}=true`),
                        "data-tracking-id": "upgrade-banner-members-page",
                        variant: "primary",
                    }}
                />
            )}
            {showEnterpriseBanner && (
                <Banner
                    variant="info"
                    text="Upgrade to Enterprise for fine-grained organizational roles, permissions and policies."
                    action={{
                        text: "Contact Sales",
                        onClick: () => window.open("https://ona.com/contact/sales", "_blank"),
                        "data-tracking-id": "upgrade-banner-groups-page",
                        variant: "primary",
                    }}
                />
            )}
            {showServiceAccountsBanner && (
                <Banner
                    variant="info"
                    text="Upgrade to Core to create service accounts to access our API."
                    action={{
                        text: "Upgrade",
                        onClick: () => navigate(`/settings/billing?${CHANGE_PLAN_QUERY_KEY}=true`),
                        "data-tracking-id": "upgrade-banner-service-accounts-page",
                        variant: "primary",
                    }}
                />
            )}
            <Tabs value={selectedTab} className="w-full" onValueChange={handleTabChange}>
                <div className="mb-4 flex w-full flex-row flex-wrap justify-between gap-4">
                    <TabsList className="w-full max-w-lg flex-grow @md:w-auto">
                        <TabsTrigger value="members" LeadingIcon={IconUser}>
                            People
                        </TabsTrigger>
                        <TabsTrigger value="groups" LeadingIcon={IconUsers}>
                            Groups
                        </TabsTrigger>
                        <TabsTrigger value="service-accounts" LeadingIcon={IconCuteRobot}>
                            Service Accounts
                        </TabsTrigger>
                    </TabsList>
                    {selectedTab === "members" && isOrgAdmin && !isFreePlan && (
                        <Button size="sm" LeadingIcon={IconUserAdd} asChild data-tracking-id="invite-members-page">
                            <Link to="/settings/members/invite">Invite</Link>
                        </Button>
                    )}
                    {selectedTab === "groups" && canCreateGroup && isEnterpriseTier && (
                        <Button
                            size="sm"
                            onClick={onAddGroup}
                            LeadingIcon={IconPlus}
                            data-tracking-id="new-group-members-page"
                        >
                            New Group
                        </Button>
                    )}
                    {selectedTab === "service-accounts" && canCreateServiceAccount && !isFreePlan && (
                        <Button
                            size="sm"
                            onClick={onAddServiceAccount}
                            LeadingIcon={IconPlus}
                            data-tracking-id="new-service-account-members-page"
                        >
                            New Service Account
                        </Button>
                    )}
                </div>
                <TabsContent value="members">
                    <MembersTab />
                </TabsContent>
                <TabsContent value="groups">
                    <GroupsTab onCreateGroup={() => setShowCreateGroupDialog(true)} canCreateGroup={canCreateGroup} />
                </TabsContent>
                <TabsContent value="service-accounts">
                    <ServiceAccountsTab
                        onCreateServiceAccount={() => setShowCreateServiceAccountDialog(true)}
                        canCreateServiceAccount={canCreateServiceAccount}
                    />
                </TabsContent>
                <CreateGroupDialog open={showCreateGroupDialog} onClose={() => setShowCreateGroupDialog(false)} />
                <CreateServiceAccountDialog
                    open={showCreateServiceAccountDialog}
                    onClose={() => setShowCreateServiceAccountDialog(false)}
                    onSuccess={(serviceAccountId) => navigate(serviceAccountDetailsUrl({ id: serviceAccountId }))}
                />
            </Tabs>
        </div>
    );
};
